import React, { memo, useCallback, useMemo } from 'react';

import { formatCitationLabel } from 'app/pages/loanApplication/components/GuidelineCitations/citationMarkers.utils';
import GuidelineExpanderCard from 'app/pages/loanApplication/components/GuidelineCitations/GuidelineExpanderCard';
import { buildCitationFromContentProps } from 'app/pages/loanApplication/tabs/aiChat/components/BotMessage/components/MessageContent/guideline.utils';
import PropTypes from 'prop-types';

import { LoadStatus } from 'constants/aiProcessRuns';

import { resolveCitationDisplayState } from '../../../../AiActivityCitations/AiActivityCitations.utils';
import { useOpenCitationFullView } from '../../../../AiActivityCitations/hooks/useOpenCitationFullView';

const ProcessCitationCard = ({ contentProps, status = LoadStatus.READY }) => {
  const openCitationFullView = useOpenCitationFullView();
  const citation = useMemo(
    () => ({
      ...buildCitationFromContentProps(contentProps),
      sectionId: contentProps.sectionId ?? null,
    }),
    [contentProps],
  );
  const { isUnavailable } = resolveCitationDisplayState(
    { sourceShortName: citation.source, sectionTitle: citation.title },
    status,
  );

  // AI Activity is read-only, so there is no chat to "Ask about this guideline"
  // — that action stays hidden. "Read Full Section" DOES apply: it opens the
  // same right-side guideline drawer chat uses, driven here by AI Activity's own
  // guideline-full-view store (isolated from the chat store so the two surfaces
  // never bleed) via the shared, non-chat-scoped guideline-section lookup. The
  // GuidelineFullView that renders it is mounted in ExecutionDetailDrawer.
  const onReadFullSection = useCallback(
    () => openCitationFullView(citation),
    [openCitationFullView, citation],
  );

  // LEN-213: a citation every source settled without (unknown ref, 403, network) is a
  // compact "unavailable" card — not a skeleton that never resolves.
  return (
    <GuidelineExpanderCard
      citation={citation}
      isUnavailable={isUnavailable}
      unavailableLabel={isUnavailable ? formatCitationLabel(null, contentProps.citationId) : null}
      onReadFullSection={onReadFullSection}
    />
  );
};

ProcessCitationCard.propTypes = {
  contentProps: PropTypes.shape({
    citationId: PropTypes.string.isRequired,
    sectionId: PropTypes.string,
    sourceId: PropTypes.string,
    sourceName: PropTypes.string,
    sourceShortName: PropTypes.string,
    sectionNumber: PropTypes.string,
    sectionTitle: PropTypes.string,
    excerpt: PropTypes.string,
  }).isRequired,
  status: PropTypes.oneOf(Object.values(LoadStatus)),
};

export default memo(ProcessCitationCard);
